import { DataConverter, FIELD_IDS } from './data-converter';
import { SpreadsheetTableFilterType, SpreadsheetTableColumnFilterOption, SpreadsheetTableColumnId, SpreadsheetTableFilterApplyType } from './table.types';

export interface FilterFieldInfo {
  fieldId: number;
  fieldName: string;
  fieldType?: string;
  isSystem: boolean;
  dropdownValues?: string[]|SpreadsheetTableColumnFilterOption[];
  colName?: string;
  typeId?: number;
  isRunPayload?: boolean;
  tableIndex?: number;
  fieldClass?: string;
  lookupValues?: Record<string, string[]> | {};
}

export interface FilterOptions {
    type: SpreadsheetTableFilterType;
    options?: SpreadsheetTableColumnFilterOption[];
    isDateField?: boolean;
    isNumberField?: boolean;
    isDropdownField?: boolean;
    isSystemField?: boolean;
    isLookupTableField?: boolean;
    lookupValues?: Record<string, string[]> | {};
    isRunPayload?: boolean;
}

// System date field IDs from Java SystemField.java
export const SYSTEM_DATE_FIELD_IDS = [
    28, // last modified
    58, // Individual Last Modified
    61, // Sample Last Modified
    64, // Marker Last Modified
    60, // Individual Created Date
    63, // Sample Created Date
    66, // Marker Created Date
    70, // Pedigree Created Date
    76, // FHQ Last Invite Sent Date
    79, // FHQ Completed Date
    82, // Date Invited
    83, // Date Started
    84, // Date Completed
    90, // Last Reminder Created
    91, // Last Reminder Sent
];
// Additional system dropdown field IDs
const SYSTEM_DROPDOWN_FIELD_IDS = [
    88, // Invite Type
    86, // Invite Status
    159, // Lock Status
    31, // Sample Audit Status
    50, // Workflow Stage
    74, // Container Type
];

// Checkbox field options for Y/N values
const CHECKBOX_FILTER_OPTIONS: SpreadsheetTableColumnFilterOption[] = [
    { actualValue: 'Y', displayValue: 'Yes' },
    { actualValue: 'N', displayValue: 'No' },
];

export const isSystemDateField = (fieldId: SpreadsheetTableColumnId): boolean => {
    if (typeof fieldId === 'number') {
        return SYSTEM_DATE_FIELD_IDS.includes(fieldId);
    }
    // If fieldId is a string, try to convert to number for comparison
    const numericFieldId = parseInt(fieldId, 10);
    return !isNaN(numericFieldId) && SYSTEM_DATE_FIELD_IDS.includes(numericFieldId);
};

/**
 * Get filter options based on field type and configuration
 */
export const getFilterOptions = (fieldInfo: FilterFieldInfo): FilterOptions => {
    const { fieldId, isSystem, dropdownValues, fieldName, typeId, isRunPayload,fieldClass } = fieldInfo;

    if (fieldName && DataConverter.hasConversion(fieldName)) {
        const fieldOptions = DataConverter.getDropdownOptions(fieldName);
        return {
            type: SpreadsheetTableFilterType.dropdown,
            options: fieldOptions,
            isDropdownField: true,
        };
    }

    // Handle system date fields first (highest priority)
    if (isSystem && SYSTEM_DATE_FIELD_IDS.includes(fieldId)) {
        return {
            type: SpreadsheetTableFilterType.date,
            isDateField: true,
            isSystemField: true,
        };
    }


  // Handle checkbox fields first (typeId 4 or fieldType Checkbox/Y/N)
  if ( typeId === 4 ) {
    return {
        type: SpreadsheetTableFilterType.checkbox,
        options: CHECKBOX_FILTER_OPTIONS
        
    };
}

    // Handle date fields - check for various date type names
    if (typeId==2) {
        return {
            type: SpreadsheetTableFilterType.date,
            isDateField: true,
        };
    }



    // Check by field class for date fields (additional fallback)
    if (fieldInfo.fieldClass && (
        fieldInfo.fieldClass.includes('Date') ||
        fieldInfo.fieldClass.includes('DateTime')
    )) {
        return {
            type: SpreadsheetTableFilterType.date,
            isDateField: true,
        };
    }

    // Handle hyperlink fields
    if (typeId === 14) {
        return {
            type: SpreadsheetTableFilterType.hyperlink,
        };
    }

    // Handle system fields with specific field IDs
    if (isSystem && fieldId === FIELD_IDS.GENDER) {
        const genderOptions = DataConverter.getDropdownOptionsById(FIELD_IDS.GENDER);
        return {
            type: SpreadsheetTableFilterType.dropdown,
            options: genderOptions,
            isSystemField: true,
            isDropdownField: true,
        };
    }
    // Handle adopted field specifically
    if (isSystem && fieldId === FIELD_IDS.ADOPTED) {
        const adoptedOptions = DataConverter.getDropdownOptionsById(FIELD_IDS.ADOPTED);
        return {
            type: SpreadsheetTableFilterType.dropdown,
            options: adoptedOptions,
            isSystemField: true,
            isDropdownField: true,
        };
    }
    // Handle additional system dropdown fields
    if (isSystem && SYSTEM_DROPDOWN_FIELD_IDS.includes(fieldId)) {
        // These fields should get their dropdown options from the API
        // For now, we'll treat them as dropdown fields without predefined options
        return {
            type: SpreadsheetTableFilterType.dropdown,
            isSystemField: true,
            isDropdownField: true,
        };
    }

    if (isSystem && fieldId === FIELD_IDS.SEX) {
        const sexOptions = DataConverter.getDropdownOptionsById(FIELD_IDS.SEX);
        return {
            type: SpreadsheetTableFilterType.dropdown,
            options: sexOptions,
            isSystemField: true,
            isDropdownField: true,
        };
    }

    if (isSystem && fieldId === FIELD_IDS.DECEASED) {
        const deceasedOptions = DataConverter.getDropdownOptionsById(FIELD_IDS.DECEASED);
        return {
            type: SpreadsheetTableFilterType.dropdown,
            options: deceasedOptions,
            isSystemField: true,
            isDropdownField: true,
        };
    }

    // Handle other system fields with yes/no options
    if (isSystem && DataConverter.hasConversionById(fieldId)) {
        const systemOptions = DataConverter.getDropdownOptionsById(fieldId);
        return {
            type: SpreadsheetTableFilterType.dropdown,
            options: systemOptions,
            isSystemField: true,
            isDropdownField: true,
        };
    }

    // Handle dropdown fields from API
    if (typeId==5 && dropdownValues && dropdownValues.length > 0) {
        if (isRunPayload) {
            if(typeof dropdownValues[0] === "object") {
                return {
                    type: SpreadsheetTableFilterType.dropdown,
                    options: dropdownValues as SpreadsheetTableColumnFilterOption[],
                    isDropdownField: true,
                };
            }
            else {
                const apiDropdownOptions = dropdownValues.map((value: string|SpreadsheetTableColumnFilterOption) => ({
                    actualValue: typeof value === 'string' ? value : value.actualValue,
                    displayValue: typeof value === 'string' ? value : value.displayValue,
                }));
                return {
                    type: SpreadsheetTableFilterType.dropdown,
                    options: apiDropdownOptions,
                    isDropdownField: true,
                };
            }
           
        } else {
            const apiDropdownOptions = dropdownValues.map((value: string|SpreadsheetTableColumnFilterOption) => ({
                actualValue: typeof value === 'string' ? value : value.actualValue,
                displayValue: typeof value === 'string' ? value : value.displayValue,
            }));
            return {
                type: SpreadsheetTableFilterType.dropdown,
                options: apiDropdownOptions,
                isDropdownField: true,
            };
        }
    }

    if (typeId==7) {
        return {
            type: SpreadsheetTableFilterType.lookupTable,
            isLookupTableField: true,
            lookupValues: fieldInfo.lookupValues || {}
        };
    }

    // Handle image fields
    if (typeId==6) {
        return {
            type: SpreadsheetTableFilterType.image,
        };
    }

    // Handle known field names with conversion constants
  
    
    
    // Strong numeric detection first
    if (typeId==30|| fieldClass=="java.lang.Integer") {
        return {
            type: SpreadsheetTableFilterType.integer,
        };
    }
    if ( typeId === 3 ) {
        return {
            type: SpreadsheetTableFilterType.number,
        };
    }

    // Default to text filter
    return {
        type: SpreadsheetTableFilterType.text,
    };
};

/**
 * Convert filter value based on field type
 */
export const convertFilterValue = (
    fieldInfo: FilterFieldInfo,
    displayValue: string
): string => {
    const { fieldId, fieldName, isSystem } = fieldInfo;

    // For system fields, convert display value to actual value
    if (isSystem && DataConverter.hasConversionById(fieldId)) {
        return String(DataConverter.toApiById(fieldId, displayValue));
    }

    // For known field names, convert display value to actual value
    if (fieldName && DataConverter.hasConversion(fieldName)) {
        return String(DataConverter.toApi(fieldName, displayValue));
    }

    // For dropdown fields from API, use the value as-is
    return displayValue;
};

/**
 * Convert filter value back to display format
 */
export const convertFilterValueToDisplay = (
    fieldInfo: FilterFieldInfo,
    actualValue: string
): string => {
    const { fieldId, fieldName, isSystem } = fieldInfo;

    // For system fields, convert actual value to display value
    if (isSystem && DataConverter.hasConversionById(fieldId)) {
        return DataConverter.toDisplayById(fieldId, actualValue);
    }

    // For known field names, convert actual value to display value
    if (fieldName && DataConverter.hasConversion(fieldName)) {
        return DataConverter.toDisplay(fieldName, actualValue);
    }

    // For dropdown fields from API, use the value as-is
    return actualValue;
};

/**
 * Create filter payload for API
 */
export const createFilterPayload = (
  fieldInfo: FilterFieldInfo,
  filterValue: any
): any => {
  const { fieldId, fieldName, typeId, isSystem} =
    fieldInfo;

  const whereClauses = [];

  if (filterValue.operator1 && (filterValue.whereClause1 ||
      filterValue.operator1 == 7 || filterValue.operator1 == 8)) {
    const value = convertFilterValue(fieldInfo, filterValue.whereClause1);
    whereClauses.push({
      fieldId: fieldId,
      fieldName: fieldName,
      type: typeId || -100,
      system: isSystem,
    //   tableIndex: tableIndex,
      operator: parseInt(filterValue.operator1),
      value: value,
    //   fieldClass: fieldClass,
      andOr:
        filterValue.andOR === "0"
          ? "AND"
          : filterValue.andOR === "1"
          ? "OR"
          : null,
    });
  }

  if (filterValue.operator2 && (filterValue.whereClause2 ||
      filterValue.operator2 == 7 || filterValue.operator2 == 8)) {
    const value = convertFilterValue(fieldInfo, filterValue.whereClause2);
    whereClauses.push({
      fieldId: fieldId,
      fieldName: fieldName,
      type: typeId || -100,
      system: isSystem,
    //   tableIndex: tableIndex,
      operator: parseInt(filterValue.operator2),
      value: value,
    //   fieldClass: fieldClass,
    });
  }

  return {
    filter: {
      whereClauses,
    },
  };
};